import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

export default function Terms() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const headingClass = isDark ? 'text-white' : 'text-slate-900';
  const subheadingClass = isDark ? 'text-white' : 'text-slate-800';
  const textClass = isDark ? 'text-gray-300' : 'text-slate-600';

  return (
    <StaticPageLayout
      title="Terms of Service"
      subtitle="Last updated: December 1, 2024"
    >
      <div className={`prose prose-lg max-w-none ${isDark ? 'prose-invert' : ''}`}>
        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>1. Acceptance of Terms</h2>
          <p className={`${textClass} mb-4`}>
            By accessing or using SweetPilot ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of the terms, you may not access the Service.
          </p>
          <p className={textClass}>
            These Terms apply to all visitors, users, and others who access or use the Service. By using the Service, you represent that you are at least 18 years old and have the legal capacity to enter into these Terms.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>2. Description of Service</h2>
          <p className={`${textClass} mb-4`}>
            SweetPilot provides AI-powered virtual employees that assist with various business tasks including but not limited to:
          </p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>Social media management and content creation</li>
            <li>Customer support and communication</li>
            <li>Data analysis and reporting</li>
            <li>Email marketing and copywriting</li>
            <li>Sales and lead generation</li>
            <li>Administrative and scheduling tasks</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>3. User Accounts</h2>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>3.1 Account Creation</h3>
          <p className={`${textClass} mb-4`}>
            To use certain features, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate.
          </p>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>3.2 Account Security</h3>
          <p className={`${textClass} mb-4`}>
            You are responsible for safeguarding your password and for all activities under your account. Notify us immediately of any unauthorized use. We are not liable for losses caused by unauthorized use of your account.
          </p>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>3.3 Account Termination</h3>
          <p className={textClass}>
            We may terminate or suspend your account at any time for violations of these Terms, without prior notice or liability.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>4. Subscription and Billing</h2>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>4.1 Pricing</h3>
          <p className={`${textClass} mb-4`}>
            Service fees are described on our pricing page. Prices are subject to change with 30 days' notice. Changes will not affect current billing cycles.
          </p>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>4.2 Credits</h3>
          <p className={`${textClass} mb-4`}>
            Credits are consumed when using AI employees. Credit costs vary by task complexity. Unused credits do not roll over to subsequent billing periods unless specified by your plan.
          </p>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>4.3 Refunds</h3>
          <p className={textClass}>
            We offer a 14-day money-back guarantee for first-time subscribers on paid plans. Refund requests must be submitted within 14 days of initial purchase. Renewals are non-refundable.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>5. Acceptable Use</h2>
          <p className={`${textClass} mb-4`}>You agree NOT to use the Service to:</p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>Violate any laws or regulations</li>
            <li>Generate illegal, harmful, or offensive content</li>
            <li>Infringe on intellectual property rights</li>
            <li>Spread misinformation or deceptive content</li>
            <li>Harass, abuse, or harm others</li>
            <li>Attempt to gain unauthorized access to systems</li>
            <li>Interfere with or disrupt the Service</li>
            <li>Use automated systems to access the Service beyond API limits</li>
            <li>Resell or redistribute the Service without authorization</li>
            <li>Generate spam or unsolicited communications</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>6. Intellectual Property</h2>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>6.1 Our Property</h3>
          <p className={`${textClass} mb-4`}>
            The Service, including its design, features, and content, is owned by SweetPilot and protected by copyright, trademark, and other laws. You may not copy, modify, or distribute our materials without permission.
          </p>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>6.2 Your Content</h3>
          <p className={`${textClass} mb-4`}>
            You retain ownership of content you create using the Service. By using the Service, you grant us a license to process and store your content as necessary to provide the Service.
          </p>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>6.3 AI-Generated Content</h3>
          <p className={textClass}>
            Content generated by AI employees based on your inputs belongs to you, subject to applicable laws. You are responsible for reviewing and verifying all AI-generated content before use.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>7. Disclaimers</h2>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>7.1 Service "As Is"</h3>
          <p className={`${textClass} mb-4`}>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
          </p>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>7.2 AI Limitations</h3>
          <p className={textClass}>
            AI employees may produce inaccurate, incomplete, or inappropriate responses. You are responsible for reviewing and verifying all outputs. Do not rely solely on AI-generated content for critical decisions.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>8. Limitation of Liability</h2>
          <p className={textClass}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, NOSLPS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>9. Indemnification</h2>
          <p className={textClass}>
            You agree to indemnify, defend, and hold harmless SweetPilot and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from your use of the Service or violation of these Terms.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>10. Governing Law</h2>
          <p className={textClass}>
            These Terms shall be governed by the laws of the State of California, without regard to its conflict of law provisions. Any disputes shall be resolved in the state or federal courts located in San Francisco County, California.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>11. Changes to Terms</h2>
          <p className={textClass}>
            We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting the updated Terms and changing the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the new Terms.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>12. Contact Information</h2>
          <p className={textClass}>
            For questions about these Terms, please contact us:
          </p>
          <ul className={`list-none mt-4 ${textClass} space-y-2`}>
            <li><strong>Email:</strong> legal@flowai.com</li>
            <li><strong>Address:</strong> SweetPilot Inc., 123 AI Street, San Francisco, CA 94102</li>
          </ul>
        </section>
      </div>
    </StaticPageLayout>
  );
}
